import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getApiKey } from '../api/client';
import type { WebEvent, CaptureLogEntry } from '@databro/shared';

const WS_RECONNECT_DELAY = 3000;

// Event subscribers for specific event types
type CaptureLogHandler = (actionId: string, captureSessionId: string, log: CaptureLogEntry) => void;
type CaptureFailedHandler = (actionId: string, captureSessionId: string, error: string) => void;
type CaptureCompletedHandler = (actionId: string, captureSessionId: string) => void;
type CaptureStartedHandler = (actionId: string, captureSessionId: string) => void;

const captureLogHandlers = new Set<CaptureLogHandler>();
const captureFailedHandlers = new Set<CaptureFailedHandler>();
const captureCompletedHandlers = new Set<CaptureCompletedHandler>();
const captureStartedHandlers = new Set<CaptureStartedHandler>();

// Subscribe to capture log events
export function subscribeToCaptureLog(handler: CaptureLogHandler): () => void {
  captureLogHandlers.add(handler);
  return () => captureLogHandlers.delete(handler);
}

// Subscribe to capture failed events
export function subscribeToCaptureFailed(handler: CaptureFailedHandler): () => void {
  captureFailedHandlers.add(handler);
  return () => captureFailedHandlers.delete(handler);
}

// Subscribe to capture completed events
export function subscribeToCaptureCompleted(handler: CaptureCompletedHandler): () => void {
  captureCompletedHandlers.add(handler);
  return () => captureCompletedHandlers.delete(handler);
}

// Subscribe to capture started events
export function subscribeToCaptureStarted(handler: CaptureStartedHandler): () => void {
  captureStartedHandlers.add(handler);
  return () => captureStartedHandlers.delete(handler);
}

export function useWebSocket() {
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const connect = useCallback(() => {
    const apiKey = getApiKey();
    if (!apiKey) {
      console.log('[WS] No API key, skipping connection');
      return;
    }

    // Build WebSocket URL (no clientId = web watcher mode)
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/api/ws?apiKey=${apiKey}`;

    console.log('[WS] Connecting to server...');
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[WS] Connected as web watcher');
      setIsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data: WebEvent = JSON.parse(event.data);
        handleEvent(data);
      } catch (error) {
        console.error('[WS] Failed to parse message:', error);
      }
    };

    ws.onclose = (event) => {
      console.log('[WS] Disconnected:', event.code, event.reason);
      setIsConnected(false);
      wsRef.current = null;

      // Schedule reconnect
      if (!reconnectTimeoutRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connect();
        }, WS_RECONNECT_DELAY);
      }
    };

    ws.onerror = (error) => {
      console.error('[WS] Error:', error);
    };
  }, []);

  const handleEvent = useCallback((event: WebEvent) => {
    console.log('[WS] Received event:', event.type);

    switch (event.type) {
      case 'capture_started':
        // Notify subscribers about capture starting
        for (const handler of captureStartedHandlers) {
          handler(event.actionId, event.captureSessionId);
        }
        break;

      case 'capture_completed':
        // Invalidate actions to refresh hasCredentials status
        queryClient.invalidateQueries({ queryKey: ['actions'] });
        // Notify subscribers
        for (const handler of captureCompletedHandlers) {
          handler(event.actionId, event.captureSessionId);
        }
        break;

      case 'capture_log':
        // Notify subscribers with the log entry
        for (const handler of captureLogHandlers) {
          handler(event.actionId, event.captureSessionId, event.log);
        }
        break;

      case 'capture_failed':
        // Notify subscribers about capture failure
        for (const handler of captureFailedHandlers) {
          handler(event.actionId, event.captureSessionId, event.error);
        }
        break;

      case 'execution_updated':
        // Invalidate both executions and actions to update status
        queryClient.invalidateQueries({ queryKey: ['executions'] });
        queryClient.invalidateQueries({ queryKey: ['actions'] });
        break;

      case 'client_status':
        // Invalidate clients to update online status
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        break;

      case 'recording_updated':
        // Refresh recording lists/detail for live match counts
        queryClient.invalidateQueries({ queryKey: ['recordings'] });
        queryClient.invalidateQueries({ queryKey: ['recording', event.recordingId] });
        break;

      case 'ping':
        // Respond to ping to keep connection alive
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'pong' }));
        }
        break;
    }
  }, [queryClient]);

  useEffect(() => {
    connect();

    return () => {
      // Cleanup on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isConnected };
}
